import { createStore } from 'react-sweet-state';

import { sortCustomImageStyles, sortGeneralImageStyles } from 'src/data/image';
import { assignVisualLanguageIdsToAsset } from 'src/data/visualLanguage';
import { createImageStyle, deleteImageStyle, updateImageStyle } from 'src/graphql/imageStyle';
import { mergeEntityIntoEntityArray } from 'src/lib/collection';

const INITIAL_STATE = {
  generalStyles: [],
  workspaceStyles: [],

  savingImageStyle: false,
  deletingImageStyle: false,
};

const PRIVATE_ACTIONS = {
  mergeStyleInCache:
    (itemToMerge) =>
    ({ getState, setState }) => {
      const { workspaceStyles: workspaceList } = getState();
      const newWorkspaceList = mergeEntityIntoEntityArray(workspaceList, itemToMerge, {
        leading: true,
      });
      setState({ workspaceStyles: newWorkspaceList });
    },

  deleteStyleFromCache:
    (itemIdToDelete) =>
    ({ getState, setState }) => {
      const { workspaceStyles: workspaceList } = getState();
      const newWorkspaceList = workspaceList.filter((item) => item.id !== itemIdToDelete);
      setState({ workspaceStyles: newWorkspaceList });
    },
};

/**
 * Public actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ setState }, { generalStyles }) => {
      setState({ generalStyles: sortGeneralImageStyles(generalStyles) });
    },

  update:
    () =>
    ({ setState }, { generalStyles }) => {
      setState({ generalStyles: sortGeneralImageStyles(generalStyles) });
    },

  initWorkspaceStyles:
    (list) =>
    ({ setState }) => {
      setState({ workspaceStyles: sortCustomImageStyles(list) });
    },

  createImageStyle:
    ({ visualLanguageId, name, resourceIds }) =>
    async ({ setState, dispatch }) => {
      setState({ savingImageStyle: true });
      try {
        const style = await createImageStyle({
          visualLanguageId,
          name,
          customerResourceIds: resourceIds,
        });

        const adjustedStyle = assignVisualLanguageIdsToAsset(style, visualLanguageId);
        dispatch(PRIVATE_ACTIONS.mergeStyleInCache(adjustedStyle));
        return adjustedStyle;
      } finally {
        setState({ savingImageStyle: false });
      }
    },

  updateImageStyle:
    ({ visualLanguageId, id, name, resourceIds, imageIndicesToRemove }) =>
    async ({ setState, dispatch }) => {
      setState({ savingImageStyle: true });
      try {
        const style = await updateImageStyle({
          id,
          name,
          customerResourceIds: resourceIds,
          referenceImagesToRemove: imageIndicesToRemove,
        });

        const adjustedStyle = assignVisualLanguageIdsToAsset(style, visualLanguageId);
        dispatch(PRIVATE_ACTIONS.mergeStyleInCache(adjustedStyle));
        return adjustedStyle;
      } finally {
        setState({ savingImageStyle: false });
      }
    },

  deleteImageStyle:
    (id) =>
    async ({ setState, dispatch }) => {
      setState({ deletingImageStyle: true });
      try {
        await deleteImageStyle({ id });
        dispatch(PRIVATE_ACTIONS.deleteStyleFromCache(id));
      } finally {
        setState({ deletingImageStyle: false });
      }
    },
};

export const ImageStyleStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'image-style-store',
});
